import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Wallet, Clock3, TrendingUp } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { PageHeader } from '../../components/shared/PageHeader';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const StatCard = ({ label, value, icon: Icon, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-card rounded-xl p-5 shadow-card border border-border"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-muted-foreground text-xs font-ui uppercase tracking-wide mb-2">{label}</p>
        <p className="font-serif text-2xl sm:text-3xl font-bold text-foreground">{value}</p>
      </div>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
    </div>
  </motion.div>
);

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `${amount.toLocaleString('sr-RS')} RSD`;
}

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async (options = {}) => {
    const { withLoading = true, silent = false } = options;
    if (withLoading) {
      setLoading(true);
    }
    adminService.getDashboard()
      .then(d => {
        setData(d);
        setError('');
      })
      .catch((err) => {
        if (!silent) {
          setError(err.message || 'Greška pri učitavanju dashboard-a');
          toast.error(err.message || 'Greška pri učitavanju dashboard-a');
        }
      })
      .finally(() => {
        if (withLoading) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadDashboard({ withLoading: false, silent: true });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [loadDashboard]);

  if (loading) {
    return (
      <div>
        <Skeleton className="h-8 w-40 mb-8" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground font-ui">{error || 'Trenutno nije moguce ucitati dashboard.'}</p>
      </div>
    );
  }

  const stats = [
    { label: 'Ukupan prihod', value: formatCurrency(data.totalRevenue), icon: Wallet, color: 'bg-emerald-50 text-emerald-700', delay: 0 },
    { label: 'Prihod u obradi', value: formatCurrency(data.pendingRevenue), icon: Clock3, color: 'bg-blue-50 text-blue-700', delay: 0.04 },
    { label: 'Prosečna porudžbina', value: formatCurrency(data.averageDeliveredOrderValue), icon: TrendingUp, color: 'bg-violet-50 text-violet-700', delay: 0.08 },
    { label: 'Nove porudžbine', value: data.newOrders, icon: ShoppingBag, color: 'bg-blue-50 text-blue-600', delay: 0.12 },
  ];

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Pregled stanja webshopa"
        actions={
          <Link to="/admin/porudžbine">
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary-light font-ui">
              Sve Porudžbine
            </Button>
          </Link>
        }
      />

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {stats.map(stat => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* Top proizvodi */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-lg font-semibold text-foreground">Top proizvodi (online)</h2>
          <Link to="/admin/porudžbine" className="text-sm text-primary font-ui hover:underline">Sve porudžbine</Link>
        </div>
        <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
          {Array.isArray(data.topProducts) && data.topProducts.length > 0 ? (
            <div className="divide-y divide-border">
              {data.topProducts.map((item, idx) => (
                <div key={item.productId || idx} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-ui text-sm font-semibold text-foreground">{item.productName}</p>
                    <p className="text-xs text-muted-foreground font-ui">
                      #{idx + 1} po prodaji
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-ui text-sm font-semibold text-foreground">{item.soldQuantity} kom</p>
                    <p className="text-xs text-muted-foreground font-ui">{item.orderCount} porudžbina</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4">
              <p className="text-sm text-muted-foreground font-ui">Još nema dovoljno podataka za top proizvode.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
